// Conquer the Flag (code id: capture-the-flag) — entry module. A 1v1 cell-territory game on a ring
// map of 7-hex cells, themed as medieval knights: each side fields a squad of five (a goalkeeper, three
// generalists and a striker; by default a Bulwark, three squires and a lancer).
//
//   types.ts — config/state/actions   board.ts — hex maths     map.ts — ring map, cells, deployment
//   rules.ts — the engine             ai.ts    — planner bot   replay.ts — replay text
//   room.ts  — the site's room wrapper

import type { AbilityId, CtfAction, CtfConfig, CtfState, EffectId, Team, UnitType } from './types';
import { TEAMS, DEFAULT_TEAM, unitText, slotLabel, TRAITS, unitLabel } from './units';
import { unitById, claimableCell, EFFECT_NAMES, TEAM_NAMES, teamOf, armyOf, SPRINT_MOVE, PASS_RANGE, REROLLS, prisoners } from './rules';
import { cellIdAt } from './map';
import { DIRECTION_NAMES, hexEq } from './board';

export * from './types';
export {
  createGame, applyAction, listLegalActions, getValidActions, isLegal, actorTeam, teamOf, playerOf,
  unitsInCell, claimNeeds, unitById, unitAt, territory, canTag, canEnter, claimableCell, cellCounts, inOwnHqCell, other,
  effectHeld, moveOf, tagBonus, weightOf, canCarry, canClaim, tagChance, tagNeed, tagBreakdown, tagWhy, EFFECT_NAMES, EFFECT_TIMING, DIE, BASE_NEED,
  resumeAfterEdit, inFreshZone, zoneHolders, zoneHexes, zoneUp, groundDefense, TEAM_NAMES, nextRng, armyOf, abilityOf, PASS_RANGE, SPRINT_MOVE, REROLLS, prisonCell, prisoners,
} from './rules';
export { buildCells, homeCell, hqOf, cellIdAt, mapIndex, UPRIGHT_ROTATION } from './map';
export { getPlannerBotAction, getFastBotAction, getRandomBotAction, evaluate } from './ai';
export { getWcStrongBotAction, getWcFastBotAction } from './bot/wc/bot';
export { getBotAction } from './site-bot';
export { encodeReplay, runReplay, parseReplay, unitCode, REPLAY_FORMAT } from './replay';
export {
  TRAITS, TRAIT_IDS, SQUIRE, LANCER, KNIGHT, SQUADS, TEAMS, DEFAULT_TEAM, SQUAD_SIZE, typeOf, hasTrait, unitIds, unitLabel, slotLabel,
  zoneAt, turnOffset, unitCost, squadCost, hexPrice, hexSector, DEFAULT_POINTS, zoneText, unitText,
} from './units';
export type { PointsTable } from './units';
export type { TagPart } from './rules';

export const GAME_NAME = 'Conquer the Flag';

// Bump RULES_VERSION when a rule or a default changes; replays and saved positions of older
// versions stop loading at each bump. 6.1 (October 2026): new built-in teams (the default Three squires
// has the Bulwark, Move 4, and a Move 7 lancer); the Magnet is the default centre outpost; options for
// army abilities (Rerolls, Pass, Sprint, Rally) and prisons (a team with its whole army in prison
// loses); each team activates its own free units. 6.2: prisoners stand on their prison cell, and one
// freed in the round it was jailed stays exhausted. 6.0: Flag Conquest only (points mode, the doubling cube, Double
// and Victory point outposts and hold-territory scoring archived: archive/POINTS_MODE.md); tags roll a
// d8 (5+ base, 2+ to 8+); defaults: centrepost (Swiftness or Watchtower), flag weight 2.
// 5.0: unit types are data (each team's squad of five: Move, a
// zone of any hexes up to 3 away, traits); squads start by slot; replay unit codes are slot numbers.
// 4.2: guard stance always on, a carrier may drop the flag
// (on its hex or next to it; exactly on its own HQ hex it scores), flag weight is any number,
// both teams respawn back-first around a taken teleporter hex. 4.1: no goalkeeper limit; guard stance and heavy flag as
// options under test. 4.0: knights theme, facing for every unit, tags and assists
// only into guard zones, every action ends the activation, outposts used as an action,
// win modes (points / conquest) and the mercy rule.
export const RULES_VERSION = '6.2';

// The built-in teams (TEAMS in units.ts), each as both teams' squads.
const both = (squad: UnitType[]): Record<Team, UnitType[]> => ({ A: squad, B: squad });
export const UNIT_PRESETS: Record<string, Pick<CtfConfig, 'squads'>> = Object.fromEntries(
  Object.entries(TEAMS).map(([k, t]) => [k, { squads: both(t.squad) }]));
export const UNIT_SET_NAMES: Record<string, string> = Object.fromEntries(Object.entries(TEAMS).map(([k, t]) => [k, t.name]));
export const OUTPOST_NAMES: Record<CtfConfig['outposts'], string> = {
  mirrored: 'Random mirrored', centre: 'Center post', none: 'Blank',
};

export const DEFAULT_CONFIG: CtfConfig = {
  rings: 2,
  ...UNIT_PRESETS[DEFAULT_TEAM],
  ownGroundDefense: 1,
  flagPickupRange: 1,
  outposts: 'centre',                // one outpost, on the centre cell
  outpostCount: 5,                   // under 'mirrored'
  effectPool: ['swift', 'watchtower'], // with no centreEffect, the centre gets one of these at random
  centreEffect: 'magnet',            // the default centre outpost
  swiftBonus: 1,
  watchtowerBonus: 1,
  mercyRounds: 0,
  dominationCells: 0,
  carrierMovePenalty: 2,             // flag weight
  abilities: { A: null, B: null },   // army abilities: none
  prisons: false,
  prisonersOnBoard: true,
};

// No hidden information: everyone sees the whole state.
export function getPlayerView(state: CtfState, playerId: string | null) {
  return { ...state, viewPlayerId: playerId };
}

// Army abilities as players read them.
export const ABILITY_NAMES: Record<AbilityId, string> = { reroll: 'Rerolls', pass: 'Pass', sprint: 'Sprint', rally: 'Rally' };
export const abilityText: Record<AbilityId, string> = {
  reroll: `Rerolls: ${REROLLS} per game, reroll a tag roll that went against you, attacking or defending; the reroll stands.`,
  pass: `Pass (once per round): as its action, the flag carrier hands the flag to a teammate up to ${PASS_RANGE} hexes away who can carry it.`,
  sprint: `Sprint (once per round): before it moves, a unit gets +${SPRINT_MOVE} Move.`,
  rally: 'Rally (once per round): a unit starting its activation in its teleporter cell moves to any other cell its team holds instead of walking, then may still act.',
};

export function describe(c: CtfConfig): string {
  const squad = (t: Team) => c.squads[t].map((u, i) =>
    `  ${String(i + 1)}. ${u.name}${slotLabel(c.squads[t], i) !== u.name ? ` (${slotLabel(c.squads[t], i)})` : ''}: ${unitText(u)}`).join('\n');
  const sameSquads = JSON.stringify(c.squads.A) === JSON.stringify(c.squads.B);
  const traitsUsed = Array.from(new Set([...c.squads.A, ...c.squads.B].flatMap(u => u.traits)));
  const pool = c.effectPool, fixed = c.centreEffect;
  const shown = c.outposts === 'centre' && fixed ? [fixed] : fixed && !pool.includes(fixed) ? [...pool, fixed] : pool;
  const where = c.outposts === 'centre' ? 'on the centre cell'
    : `on the centre cell and ${(c.outpostCount - 1) / 2} pairs of cells mirrored across it, chosen at random`;
  return [
    `${GAME_NAME.toUpperCase()} v${RULES_VERSION}`,
    '',
    `The map is a centre cell plus ${c.rings} rings of 7-hex cells. Each team owns its HQ cell (the flag) and its teleporter cell; every other cell starts neutral.`,
    'The first team to carry the enemy flag onto its own HQ hex wins.',
    '',
    'Units (all move by facing: each point of Move steps one hex forward or turns 60°; slot 1 starts in goal, slots 2–5 at the teleporter):',
    sameSquads ? squad('A') : `Blue:\n${squad('A')}\nRed:\n${squad('B')}`,
    traitsUsed.length ? `Traits: ${traitsUsed.map(id => `${TRAITS[id].name}: ${TRAITS[id].text}`).join(' ')}` : '',
    '',
    'Each round: each team activates each of its units once; players alternate, and a side with activations left after the other has none plays them in a row; the first player alternates each round (round 1: drawn at random).',
    `Activation: move, then take at most one action, which ends the activation: tag, pick up the flag, claim the cell you stand in, use the outpost you stand on, stand guard, or drop the flag.`,
    'Guard zones: a unit can only tag enemies inside its guard zone (a lancer strikes through a unit in front). While a unit hasn\'t activated this round, an enemy that moves into its zone is stopped: it can\'t move or turn again that activation, but it can still act. A unit that starts its activation inside an enemy zone may turn and move out freely.',
    'Guard: as its action, a unit may stand guard: its guard zone keeps stopping enemies for the rest of the round.',
    'Drop the flag: as its action, the carrier may put the flag down on its own hex or one next to it; put down exactly on its own HQ hex, it wins.',
    c.carrierMovePenalty > 0 ? `Flag weight: the flag carrier has ${c.carrierMovePenalty} less Move (at least 1).` : '',
    'Claim: the cell must touch your ground and you need more of your units in it than the enemy has. Home cells can never be claimed; enemy cells can be flipped.',
    `Tag: roll a d8, 5+${c.ownGroundDefense ? `, ${c.ownGroundDefense} harder against a unit on its own ground` : ''}; 1 easier for each other unit of yours whose guard zone covers the target, 1 harder for each of its teammates whose guard zone covers the attacker (activated units count too; 2+ at best, 8+ at worst). A tagged unit that hasn't activated this round loses its activation, drops the flag where it stands and respawns in its teleporter cell.`,
    c.outposts === 'none' ? 'No outposts.'
      : `${c.outposts === 'centre' && fixed ? `Outpost: one, on the centre cell: ${EFFECT_NAMES[fixed]}.` : `Outposts: dealt at random at the start ${where}${fixed ? ` (the centre one is always ${EFFECT_NAMES[fixed]})` : ''}.`} Static effects work while you hold the cell; active ones are used as the action of a unit standing on the outpost (centre hex) of a cell you hold.\n${shown.map(e => `  ${effectText(c)[e]}`).join('\n')}`,
    c.dominationCells > 0 ? `Domination: a team holding ${c.dominationCells} or more cells at the end of a round wins the game.` : '',
    c.mercyRounds > 0 ? `Mercy rule: if the game reaches ${c.mercyRounds} rounds, it ends; the team holding more cells wins (otherwise a draw).` : '',
    c.prisons ? `Prisons: a unit tagged on a cell the enemy holds goes to its team's prison cell (Blue cell 9, Red cell 15 on the standard map) instead of the teleporter. Prisoners stand in that cell (they block their hex but can't be tagged, have no zone and don't count for claiming). A teammate standing in that cell may free all its prisoners as its action (Jailbreak); they can act this round unless they were jailed this round. Prisoners stay until freed and sit out the rounds they spend there. A team with its whole army in prison loses.` : '',
    ...(['A', 'B'] as Team[]).map(t => { const id = c.abilities?.[t]; return id ? `${TEAM_NAMES[t]}'s army ability: ${abilityText[id]}` : ''; }),
  ].filter(Boolean).join('\n');
}

export const effectText = (c: CtfConfig): Record<EffectId, string> => ({
  swift: `Swiftness (static): your units get +${c.swiftBonus} Move.`,
  watchtower: `Watchtower (static): your tags against units inside cells you hold are ${c.watchtowerBonus} easier.`,
  longShot: 'Long shot (active): the unit on the outpost tags any enemy unit anywhere.',
  blink: 'Blink (active): move one of your units (not the flag carrier) to any cell you hold.',
  magnet: 'Magnet (static): at the start of each round after the first, the enemy flag (if no one carries it) moves 1 hex toward your HQ, never onto the HQ hex itself.',
});

export function describeAction(s: CtfState, a: CtfAction): string {
  const nm = (id: string) => { try { const u = unitById(s, id); return `${TEAM_NAMES[u.team]} ${unitLabel(s, u)}`; } catch { return id; } };
  switch (a.type) {
    case 'activate': return `Activate ${nm(a.unitId)}`;
    case 'move': return `Move to ${a.to.q},${a.to.r}`;
    case 'turn': return a.dir === 1 ? 'Turn anticlockwise' : 'Turn clockwise';
    case 'tag': return `Tag ${nm(a.targetId)}`;
    case 'pickUp': return 'Pick up the flag';
    case 'claim': {
      const id = s.active ? claimableCell(s, unitById(s, s.active.unitId)) : null;
      const fx = id !== null ? s.effects?.[id] : null;
      return id !== null ? `Claim cell ${id}${fx ? ` (${EFFECT_NAMES[fx]})` : ''}` : 'Claim this cell';
    }
    case 'guard': return 'Stand guard (zone up until the round ends)';
    case 'dropFlag': {
      const from = s.active ? unitById(s, s.active.unitId).pos : null;
      return from && hexEq(from, a.to) ? 'Drop the flag here' : `Drop the flag at ${a.to.q},${a.to.r}`;
    }
    case 'useOutpost': {
      const u = s.active ? unitById(s, s.active.unitId) : null;
      const id = u?.pos ? cellIdAt(s.cells, u.pos) : undefined;
      const fx = id !== undefined ? s.effects[id] : null;
      return `Use the ${fx ? EFFECT_NAMES[fx] : ''} outpost`.replace('  ', ' ');
    }
    case 'endActivation': return 'End activation';
    case 'longShot': return `Long shot at ${nm(a.targetId)}`;
    case 'blink': return `Blink ${nm(a.unitId)} to cell ${a.cell}`;
    case 'skipEffect': return 'Pass on the effect';
    case 'resign': return 'Resign';
    case 'reroll': { const t = teamOf(s, a.playerId); return `Reroll${t ? ` (${armyOf(s, t).rerolls} left)` : ''}`; }
    case 'keepRoll': return s.pending?.kind === 'reroll' ? `Keep the ${s.pending.hit ? 'hit' : 'miss'}` : 'Keep the roll';
    case 'pass': return `Pass the flag to ${nm(a.toUnitId)}`;
    case 'sprint': return `Sprint (+${SPRINT_MOVE} Move)`;
    case 'rally': return `Rally to cell ${a.cell}`;
    case 'jailbreak': { const t = teamOf(s, a.playerId); const n = t ? prisoners(s, t).length : 0; return `Jailbreak: free ${n} prisoner${n === 1 ? '' : 's'}`; }
  }
}

export const FACING_NAMES = DIRECTION_NAMES;
export type { Team };
